// ABOUTME: Chart 3 of FamilyInsightsPage — day-of-week heatmap or week bar chart
// ABOUTME: Issue #1607: heatmap (struggle intensity) for non-Week tabs, bar chart for Week tab

import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import naluV1Theme from '../theme/theme.nalu-v1';
import { BehaviorType } from '../types/api';
import { analyticsAPI } from '../services/api';
import { getInsightsDateRange } from '../utils/dateUtils';
import { getBehaviorColor } from '../utils/behaviorUtils';

interface BehaviorBreakdownPoint {
  period: string;
  stars: number;
  triangles: number;
  squares: number;
  flipYourCoins: number;
  makeItRights: number;
  oneOnOneTimes: number;
}

interface DayOfWeekPatternChartProps {
  familyId: number;
  selectedChildId: number | 'all';
  activeTabLabel: string;
  familyTimezone: string | undefined;
  familyCreatedAt: string | undefined;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const BEHAVIOR_CONFIG = [
  { key: 'stars' as const, color: getBehaviorColor(BehaviorType.Star) },
  { key: 'triangles' as const, color: getBehaviorColor(BehaviorType.Triangle) },
  { key: 'squares' as const, color: getBehaviorColor(BehaviorType.Square) },
  { key: 'flipYourCoins' as const, color: getBehaviorColor(BehaviorType.FlipYourCoin) },
  { key: 'makeItRights' as const, color: getBehaviorColor(BehaviorType.MakeItRight) },
  { key: 'oneOnOneTimes' as const, color: getBehaviorColor(BehaviorType.OneOnOneTime) },
] as const;

// Helper to convert hex color to rgb values for rgba() usage
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
    : { r: 0, g: 0, b: 0 };
}

const DayOfWeekPatternChart: React.FC<DayOfWeekPatternChartProps> = ({
  familyId,
  selectedChildId,
  activeTabLabel,
  familyTimezone,
  familyCreatedAt,
}) => {
  const theme = useTheme();
  // Compared by value (logoSrc), not object identity — dev-server HMR can give the
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // object references after a hot-reload even though both are "the nalu-v1 theme".
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [rawData, setRawData] = useState<BehaviorBreakdownPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Always use granularity=day so we have daily data to aggregate by day-of-week
        const { startDate, endDate } = getInsightsDateRange(
          activeTabLabel,
          familyTimezone,
          familyCreatedAt
        );
        const childId = selectedChildId === 'all' ? undefined : selectedChildId;
        const data = await analyticsAPI.getBehaviorBreakdownOverTime(
          familyId,
          startDate,
          endDate,
          'day',
          familyTimezone,
          childId
        );
        setRawData(data);
      } catch (err) {
        console.error('Failed to load behavior breakdown data:', err);
        setError('Failed to load day-of-week pattern data');
      } finally {
        setLoading(false);
      }
    };

    if (familyId) {
      fetchData();
    }
  }, [familyId, selectedChildId, activeTabLabel, familyTimezone, familyCreatedAt]);

  // Aggregate by day of week (0=Sun .. 6=Sat) for heatmap
  const dayOfWeekData = useMemo(() => {
    const aggregated = Array.from({ length: 7 }, (_, i) => ({
      dayOfWeek: i,
      day: DAY_NAMES[i],
      stars: 0,
      triangles: 0,
      squares: 0,
      flipYourCoins: 0,
      makeItRights: 0,
      oneOnOneTimes: 0,
      struggle: 0, // triangles + squares
    }));

    rawData.forEach(point => {
      const date = new Date(point.period + 'T12:00:00');
      const dow = date.getDay(); // 0=Sun
      aggregated[dow].stars += point.stars;
      aggregated[dow].triangles += point.triangles;
      aggregated[dow].squares += point.squares;
      aggregated[dow].flipYourCoins += point.flipYourCoins;
      aggregated[dow].makeItRights += point.makeItRights;
      aggregated[dow].oneOnOneTimes += point.oneOnOneTimes;
      aggregated[dow].struggle += point.triangles + point.squares;
    });

    return aggregated;
  }, [rawData]);

  // Week tab: daily bar chart data for all 7 days in the range (zeros for days with no data)
  const weekBarData = useMemo(() => {
    const { startDate, endDate } = getInsightsDateRange(
      activeTabLabel,
      familyTimezone,
      familyCreatedAt
    );
    const dataByPeriod = new Map(rawData.map(p => [p.period, p]));
    const result = [];
    const cursor = new Date(startDate + 'T12:00:00');
    const end = new Date(endDate + 'T12:00:00');
    while (cursor <= end) {
      const pad = (n: number) => n.toString().padStart(2, '0');
      const period = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}-${pad(cursor.getDate())}`;
      const point = dataByPeriod.get(period) ?? {
        period,
        stars: 0, triangles: 0, squares: 0,
        flipYourCoins: 0, makeItRights: 0, oneOnOneTimes: 0,
      };
      result.push({
        day: cursor.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' }),
        ...point,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return result;
  }, [rawData, activeTabLabel, familyTimezone, familyCreatedAt]);

  const hasData = rawData.length > 0;
  const primaryRgb = hexToRgb(theme.palette.primary.main);
  const maxStruggle = Math.max(...dayOfWeekData.map(d => d.struggle));

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant={isNaluV1Theme ? 'h4' : 'h6'} gutterBottom>
        Day-of-Week Pattern
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress role="progressbar" />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {!loading && !error && !hasData && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <Typography color="text.secondary">
            No data available for this period. Log behaviors to see day-of-week patterns.
          </Typography>
        </Box>
      )}

      {!loading && !error && hasData && activeTabLabel === 'Week' && (
        <Box data-testid="dow-week-chart">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={weekBarData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" fontSize={9} angle={-30} textAnchor="end" height={50} />
              <YAxis fontSize={10} />
              <Tooltip />
              {BEHAVIOR_CONFIG.map(({ key, color }) => (
                <Bar key={key} dataKey={key} stackId="behaviors" fill={color} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      {!loading && !error && hasData && activeTabLabel !== 'Week' && (
        <Box data-testid="dow-heatmap" sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textAlign: 'center' }}>
            Struggle intensity by day of week (triangles + squares)
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            {dayOfWeekData.map((data) => {
              // When maxStruggle is 0, intensity=0 for all boxes → minimum opacity (no divide-by-zero)
              const intensity = maxStruggle > 0 ? data.struggle / maxStruggle : 0;
              const opacity = intensity * 0.8 + 0.1; // range: 0.1 (min) to 0.9 (max)
              const backgroundColor = `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, ${opacity})`;
              return (
                <Box
                  key={data.dayOfWeek}
                  data-testid={`dow-box-${data.dayOfWeek}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 36, sm: 48 },
                      height: { xs: 36, sm: 48 },
                      backgroundColor,
                      border: '1px solid',
                      borderColor: 'divider',
                      // Bare numbers are a multiplier against theme.shape.borderRadius (16
                      // for nalu-v1) under the sx prop, so an explicit px string is required.
                      borderRadius: isNaluV1Theme ? '4px' : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: 8, sm: 10 },
                        color: intensity > 0.5 ? 'white' : 'text.primary',
                      }}
                    >
                      {data.struggle}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: { xs: 9, sm: 11 } }}>
                    {data.day}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DayOfWeekPatternChart;
